/** Metric-card spots. The Teams page banner is not one of these. */
const METRIC_LOCATION_SLOT_COUNT = 3;

/**
 * Metric cards on the merchant Support dashboard tab, in page order.
 * Titles match RedoMetricItem titles in the support dashboard.
 */
export const supportInsightLocations = [
  {
    id: "ticket-automation-rate",
    label: "Ticket automation rate",
    recommended: true,
  },
  { id: "total-tickets-automated", label: "Total tickets automated" },
  {
    id: "potential-cost-savings",
    label: "Potential cost savings",
    recommended: true,
  },
  { id: "total-tickets", label: "Total tickets" },
  { id: "created-tickets", label: "Created tickets" },
  { id: "closed-tickets", label: "Closed tickets" },
  { id: "resolved-tickets", label: "Resolved tickets" },
  { id: "median-first-response-time", label: "Median first response time" },
  { id: "median-first-human-response", label: "Median first human response" },
  {
    id: "median-time-to-resolution",
    label: "Median time to resolution",
    recommended: true,
  },
] as const;

export type SupportInsightLocationId =
  (typeof supportInsightLocations)[number]["id"];

/** First three metric cards, for the configurable location spots. */
export function defaultInsightLocations(): SupportInsightLocationId[] {
  return supportInsightLocations
    .slice(0, METRIC_LOCATION_SLOT_COUNT)
    .map((location) => location.id);
}

/** Puts `id` in `index`. A card already used by another spot stays where it is. */
export function assignInsightLocation(
  locations: readonly SupportInsightLocationId[],
  index: number,
  id: SupportInsightLocationId,
): SupportInsightLocationId[] {
  const next = defaultInsightLocations().map(
    (fallback, slot) => locations[slot] ?? fallback,
  );
  const current = next[index];
  if (current === undefined || current === id) {
    return next;
  }
  const taken = next.some((value, slot) => slot !== index && value === id);
  if (taken) {
    return next;
  }
  next[index] = id;
  return next;
}

export function insightLocationsUsedElsewhere(
  locations: readonly SupportInsightLocationId[],
  index: number,
): { value: SupportInsightLocationId; tooltip: string }[] {
  return locations.flatMap((id, slot) =>
    slot === index
      ? []
      : [{ value: id, tooltip: "Already chosen for another insight." }],
  );
}
